import { makeAutoObservable, flow } from 'mobx';
import { toast } from 'react-toastify';
import API from '../api/client';
import { MenuItem, Menu, ModifierGroup, Modifier } from '../types';

class CloverMenuStore {
  items: MenuItem[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    // `autoBind: true` automatically binds non-arrow methods.
    // In this example, `fetchCloverMenu` is an arrow function, so it doesn’t need binding.
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // 1. Use `flow` to wrap the generator function
  //    This ensures any mutations to observables happen in an action context,
  //    even after async yields.
  fetchCloverMenu = flow(function* (this: CloverMenuStore) {
    this.loading = true;
    this.error = null;

    try {
      // 2. `yield` replaces `await`; MobX ensures
      //    subsequent code still runs in an action.
      const response = yield API.get<Menu[]>('/api/menus/');
      const allMenus: Menu[] = response.data;

      if (!Array.isArray(allMenus) || allMenus.length === 0) {
        throw new Error('No menus returned from the API');
      }

      const cloverMenu = allMenus.find(
        (m) => m.name === 'Santa Playa Clover Menu',
      );
      if (!cloverMenu) {
        throw new Error('Could not find "Santa Playa Clover Menu".');
      }

      // Flatten categories/subcategories → items
      const flattened: MenuItem[] = [];
      cloverMenu.categories.forEach((cat) => {
        cat.menu_items.forEach((item) => {
          flattened.push(this.buildFlattenedItem(item, cat.name, undefined));
        });
        cat.subcategories.forEach((subcat) => {
          subcat.menu_items.forEach((item) => {
            flattened.push(
              this.buildFlattenedItem(item, cat.name, subcat.name),
            );
          });
        });
      });

      // 3. Safe to mutate observable state (still in an action)
      this.items = flattened;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Error fetching Clover menu:', err);
      this.error = err.message || 'Failed to fetch Clover menu.';
      toast.error(this.error);
    } finally {
      this.loading = false;
    }
  });

  // Helper method: modifies or returns data but not async, so no need for flow
  private buildFlattenedItem(
    item: MenuItem,
    categoryName: string,
    subcategoryName?: string,
  ): MenuItem {
    return {
      ...item,
      base_price: parsePrice(item.base_price),
      categoryName,
      subcategoryName,
      modifier_groups: item.modifier_groups
        ? item.modifier_groups.map(parseModifierGroup)
        : [],
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parsePrice(price: any): number | undefined {
  if (price == null) return undefined;
  if (typeof price === 'number') return price;
  const parsed = parseFloat(price);
  return isNaN(parsed) ? undefined : parsed;
}

function parseModifier(mod: Modifier): Modifier {
  return {
    ...mod,
    price: typeof mod.price === 'string' ? parseFloat(mod.price) : mod.price,
  };
}

function parseModifierGroup(mg: ModifierGroup): ModifierGroup {
  return {
    ...mg,
    force_popup: !!mg.force_popup,
    min_required: mg.min_required || 0,
    max_allowed: mg.max_allowed || 99,
    modifiers: mg.modifiers ? mg.modifiers.map(parseModifier) : [],
  };
}

const cloverMenuStore = new CloverMenuStore();
export default cloverMenuStore;
